import React from 'react';
import styled from 'styled-components';
import {Alert, Checkbox, Icon} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {GroupLabel, LabelledToggle, Note, Section, Sections, SettingStack} from './common';
import type {PanelProps} from './types';
import type {QuoteRollup, QuoteTemplate} from '../resolver/types';

const TypeGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 8px;
	margin-bottom: 12px;
`;

const cardFace = `
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 3px;
	padding: 12px;
	border-radius: 8px;
	text-align: left;
`;

const TypeCard = styled.button<{$selected: boolean}>`
	${cardFace};
	border: 1px solid
		${({theme, $selected}) => ($selected ? theme.sys.color.brand.primary['1'] : theme.sys.color.border.decorative)};
	background: ${({theme, $selected}) =>
		$selected ? theme.sys.color.background.primary['1'] : theme.sys.color.background.neutral['1']};
	cursor: pointer;
`;

const DisabledCard = styled.div`
	${cardFace};
	border: 1px dashed ${({theme}) => theme.sys.color.border.decorative};
	background: ${({theme}) => theme.sys.color.background.disabled['1']};
	cursor: not-allowed;
`;

const CardTitle = styled.span`
	font-size: 13px;
	font-weight: 600;
`;

const CardHint = styled.span`
	font-size: 11px;
	color: ${({theme}) => theme.sys.color.text.content.auxiliary};
`;

const CheckboxRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 10px;
	font-size: 13px;
`;

const ROLLUP_CARDS: Array<{value: QuoteRollup; labelId: string; hintId: string}> = [
	{value: 'roles', labelId: 'quote_designer.estimate.rollup.roles', hintId: 'quote_designer.estimate.rollup.roles_hint'},
	{value: 'phase', labelId: 'quote_designer.estimate.rollup.phase', hintId: 'quote_designer.estimate.rollup.phase_hint'},
	{
		value: 'deliverable',
		labelId: 'quote_designer.estimate.rollup.deliverable',
		hintId: 'quote_designer.estimate.rollup.deliverable_hint',
	},
	{
		value: 'project',
		labelId: 'quote_designer.estimate.rollup.project',
		hintId: 'quote_designer.estimate.rollup.project_hint',
	},
];

/**
 * How the estimate table is rolled up, which columns it shows, and which totals it prints.
 *
 * The design draws one "Expenses per Phase" toggle where the template carries two independent
 * settings and omits deliverable headers entirely; every existing control is kept, because
 * building the design literally would silently remove two the user has today.
 */
const EstimatePanel: React.FC<PanelProps> = ({template, onChange, data}) => {
	const intl = useIntl();
	const setColumns = (patch: Partial<QuoteTemplate['estimate']['columns']>) =>
		onChange({...template, estimate: {...template.estimate, columns: {...template.estimate.columns, ...patch}}});
	const setGrouping = (patch: Partial<QuoteTemplate['estimate']['grouping']>) =>
		onChange({...template, estimate: {...template.estimate, grouping: {...template.estimate.grouping, ...patch}}});
	const setTotals = (patch: Partial<QuoteTemplate['estimate']['totals']>) =>
		onChange({...template, estimate: {...template.estimate, totals: {...template.estimate.totals, ...patch}}});
	const setRollup = (rollup: QuoteRollup) => onChange({...template, estimate: {...template.estimate, rollup}});

	const rolesRollup = template.estimate.rollup === 'roles';
	// An expense-only baseline has no role rows, so the role-totals toggle would change nothing
	// visible — disabled rather than looking dead.
	const hasRoleItems = data.lineItems.some(item => item.kind === 'role');
	const days = data.estimationUnit === 'days';
	const timeLabel = intl.formatMessage({id: days ? 'common.days' : 'common.hours'});
	// Rolled up past roles there is no single role's rate left to show, so the column is a blend.
	const rateLabel = intl.formatMessage({
		id: rolesRollup ? 'quote_designer.estimate.show_rate' : 'quote_designer.estimate.show_blended_rate',
	});

	return (
		<Sections $ruled>
			<Section>
				<GroupLabel>{intl.formatMessage({id: 'quote_designer.estimate.type'})}</GroupLabel>
				<TypeGrid>
					{ROLLUP_CARDS.map(card =>
						card.value === 'deliverable' && !data.useDeliverables ? (
							<DisabledCard key={card.value} data-cy="quote-rollup-deliverable-disabled">
								<CardTitle>
									<Icon icon="lockClosed" size="s" color="disabled" />
									{intl.formatMessage({id: card.labelId})}
								</CardTitle>
								<CardHint>
									{intl.formatMessage({id: 'quote_designer.estimate.rollup.deliverable_off'})}
								</CardHint>
							</DisabledCard>
						) : (
							<TypeCard
								key={card.value}
								type="button"
								$selected={template.estimate.rollup === card.value}
								onClick={() => setRollup(card.value)}
								data-cy={`quote-rollup-${card.value}`}
							>
								<CardTitle>{intl.formatMessage({id: card.labelId})}</CardTitle>
								<CardHint>{intl.formatMessage({id: card.hintId})}</CardHint>
							</TypeCard>
						)
					)}
				</TypeGrid>
				{!data.useDeliverables && (
					<Alert
						type={Alert.TYPE.INFORMATION}
						text={intl.formatMessage({id: 'quote_designer.estimate.deliverables_alert'})}
					/>
				)}
			</Section>

			{/* Owned by Baseline Settings: locked, and saying which unit it is locked to. */}
			<Section data-cy="quote-effort-display">
				<LabelledToggle
					label={intl.formatMessage({id: 'quote_designer.estimate.effort_display'}, {unit: timeLabel})}
					description={intl.formatMessage({id: 'quote_designer.estimate.time_note'}, {unit: timeLabel})}
					trailing={<Icon icon="lockClosed" size="s" color="auxiliary" />}
					headingLabel
					checked
					disabled
					onChange={() => undefined}
					cy="quote-effort-display-toggle"
				/>
			</Section>

			<Section>
				<GroupLabel>{intl.formatMessage({id: 'quote_designer.estimate.columns'})}</GroupLabel>
				<SettingStack>
					<CheckboxRow>
						<Checkbox
							checked={template.estimate.columns.time}
							onChange={(checked: boolean) => setColumns({time: checked})}
							aria-label={timeLabel}
							data-cy="quote-column-time"
						/>
						<span>{timeLabel}</span>
					</CheckboxRow>
					<CheckboxRow>
						<Checkbox
							checked={template.estimate.columns.rate}
							onChange={(checked: boolean) => setColumns({rate: checked})}
							aria-label={rateLabel}
							data-cy="quote-column-rate"
						/>
						<span>{rateLabel}</span>
					</CheckboxRow>
					<CheckboxRow>
						<Checkbox
							checked={template.estimate.columns.amount}
							onChange={(checked: boolean) => setColumns({amount: checked})}
							aria-label={intl.formatMessage({id: 'quote_designer.estimate.show_amount'})}
							data-cy="quote-column-amount"
						/>
						<span>{intl.formatMessage({id: 'quote_designer.estimate.show_amount'})}</span>
					</CheckboxRow>
				</SettingStack>
			</Section>

			{/*
			 * Every roll-up toggle here shapes the per-role table: role amounts, phase subtotals,
			 * a phase's expense figure, deliverable headings. Rolled up any further the table is one
			 * line per group and none of them have anything left to act on, so the design drops the
			 * whole group rather than showing four controls that change nothing.
			 */}
			{rolesRollup && (
				<Section>
					<GroupLabel>{intl.formatMessage({id: 'quote_designer.estimate.rollups'})}</GroupLabel>
					<SettingStack>
						<LabelledToggle
							label={intl.formatMessage({id: 'quote_designer.estimate.show_role_totals'})}
							description={intl.formatMessage({id: 'quote_designer.estimate.show_role_totals_hint'})}
							checked={template.estimate.grouping.roleTotals}
							disabled={!hasRoleItems}
							onChange={checked => setGrouping({roleTotals: checked})}
							cy="quote-toggle-role-totals"
						/>
						<LabelledToggle
							label={intl.formatMessage({id: 'quote_designer.estimate.show_phase_subtotals'})}
							description={intl.formatMessage({id: 'quote_designer.estimate.show_phase_subtotals_hint'})}
							checked={template.estimate.grouping.phaseSubtotals}
							onChange={checked => setGrouping({phaseSubtotals: checked})}
							cy="quote-toggle-phase-subtotals"
						/>
						<LabelledToggle
							label={intl.formatMessage({id: 'quote_designer.estimate.show_phase_expenses'})}
							description={intl.formatMessage({id: 'quote_designer.estimate.show_phase_expenses_hint'})}
							checked={template.estimate.grouping.phaseExpenses}
							onChange={checked => setGrouping({phaseExpenses: checked})}
							cy="quote-toggle-phase-expenses"
						/>
						{data.useDeliverables && (
							<LabelledToggle
								label={intl.formatMessage({id: 'quote_designer.estimate.show_deliverables'})}
								description={intl.formatMessage({id: 'quote_designer.estimate.show_deliverables_hint'})}
								checked={template.estimate.grouping.deliverableHeaders}
								onChange={checked => setGrouping({deliverableHeaders: checked})}
								cy="quote-toggle-deliverables"
							/>
						)}
					</SettingStack>
				</Section>
			)}

			<Section>
				<GroupLabel>{intl.formatMessage({id: 'quote_designer.estimate.totals'})}</GroupLabel>
				<SettingStack>
					<LabelledToggle
						label={intl.formatMessage({id: 'quote_designer.estimate.show_subtotal'})}
						checked={template.estimate.totals.subtotal}
						onChange={checked => setTotals({subtotal: checked})}
						cy="quote-toggle-subtotal"
					/>
					<LabelledToggle
						label={intl.formatMessage({id: 'quote_designer.estimate.show_expenses'})}
						checked={template.estimate.totals.expenses}
						onChange={checked => setTotals({expenses: checked})}
						cy="quote-toggle-expenses-row"
					/>
					<LabelledToggle
						label={intl.formatMessage({id: 'quote_designer.estimate.show_grand_total'})}
						checked={template.estimate.totals.grandTotal}
						onChange={checked => setTotals({grandTotal: checked})}
						cy="quote-toggle-grand-total"
					/>
				</SettingStack>
				<Note>{intl.formatMessage({id: 'quote_designer.estimate.note'})}</Note>
			</Section>
		</Sections>
	);
};

export default EstimatePanel;
